import { useEffect, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Filter, Plus, QrCode, Search, ShoppingCart } from 'lucide-react'

import { toast } from 'sonner'
import type { SalesOrderStatus } from '@/lib/api/sales-order-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { OrderStatusBadge } from '@/components/sections/order-status-badge'
import { QRScannerDialog } from '@/components/sections/qr-scanner-dialog'
import { SLABadge } from '@/components/sla-badge'
import { useSalesOrders } from '@/hooks/use-sales-order-query'
import { useDebounced } from '@/hooks/use-debounced'
import { useAuth } from '@/hooks/use-auth'
import {
    LIST_SEARCH_KEYS,
    useRememberListSearch,
} from '@/lib/list-search-store'
import { m } from '@/lib/paraglide/messages'
import { formatVnd } from '@/lib/format'
import { salesOrderApi } from '@/lib/api/sales-order-api'
import {
    downloadBlob,
    filenameFromContentDisposition,
} from '@/lib/download-blob'

type PaymentFilter = 'unpaid' | 'partial' | 'paid'
type SettlementFilter = 'cash' | 'bank_transfer' | 'credit'

export interface OrdersSearch {
    q?: string
    status?: SalesOrderStatus
    payment_status?: PaymentFilter
    settlement_intent?: SettlementFilter
    date_from?: string
    date_to?: string
    page?: number
}

const ORDER_STATUS_VALUES: readonly string[] = [
    'new',
    'awaiting_packaging',
    'confirmed',
    'stock_out',
    'ready_to_deliver',
    'in_delivery',
    'delivered',
    'delivery_failed',
    'returned',
    'cancelled',
]

export const Route = createFileRoute('/_app/_authed/_sidebar/orders/')({
    component: OrdersPage,
    // Trang + toàn bộ filter đẩy lên search param → reload / share link / back
    // giữ nguyên trạng thái. Cũng hỗ trợ deep-link từ dashboard (?payment_status=…).
    validateSearch: (search: Record<string, unknown>): OrdersSearch => {
        const out: OrdersSearch = {}
        if (typeof search.q === 'string' && search.q) out.q = search.q
        if (
            typeof search.status === 'string' &&
            ORDER_STATUS_VALUES.includes(search.status)
        ) {
            out.status = search.status as SalesOrderStatus
        }
        const ps = search.payment_status
        if (ps === 'unpaid' || ps === 'partial' || ps === 'paid') {
            out.payment_status = ps
        }
        const si = search.settlement_intent
        if (si === 'cash' || si === 'bank_transfer' || si === 'credit') {
            out.settlement_intent = si
        }
        if (typeof search.date_from === 'string' && search.date_from) {
            out.date_from = search.date_from
        }
        if (typeof search.date_to === 'string' && search.date_to) {
            out.date_to = search.date_to
        }
        const p =
            typeof search.page === 'number' ? search.page : Number(search.page)
        if (Number.isFinite(p) && p > 1) out.page = p
        return out
    },
})

const PAYMENT_FILTER_OPTIONS: { value: string; label: string }[] = [
    { value: 'all', label: m.order_payment_all() },
    { value: 'unpaid', label: m.order_payment_unpaid() },
    { value: 'partial', label: m.order_payment_partial() },
    { value: 'paid', label: m.order_payment_paid() },
]

const SETTLEMENT_INTENT_OPTIONS: { value: string; label: string }[] = [
    { value: 'all', label: m.order_settlement_filter_all() },
    { value: 'cash', label: m.payment_method_cash() },
    { value: 'bank_transfer', label: m.payment_method_bank() },
    { value: 'credit', label: m.order_settlement_credit() },
]

const STATUS_OPTIONS: { value: string; label: string }[] = [
    { value: 'all', label: m.order_status_all() },
    { value: 'new', label: m.status_order_new() },
    { value: 'awaiting_packaging', label: m.await_filter_option() },
    { value: 'confirmed', label: m.status_order_confirmed() },
    { value: 'stock_out', label: m.status_order_stock_out() },
    { value: 'ready_to_deliver', label: m.status_order_ready_to_deliver() },
    { value: 'in_delivery', label: m.status_order_in_delivery() },
    { value: 'delivered', label: m.status_order_delivered() },
    { value: 'delivery_failed', label: m.status_order_delivery_failed() },
    { value: 'returned', label: m.dashboard_status_returned() },
    { value: 'cancelled', label: m.status_order_cancelled() },
]

const PAYMENT_LABELS: Record<string, string> = {
    unpaid: m.order_payment_short_unpaid(),
    partial: m.order_payment_short_partial(),
    paid: m.order_payment_short_paid(),
}

function getChannelLabel(channel: string): string {
    switch (channel) {
        case 'direct':
            return m.order_channel_direct()
        case 'phone':
            return m.order_channel_phone()
        case 'online':
            return m.order_channel_online()
        case 'agent':
            return m.order_channel_agent()
        case 'quick_order':
            return m.order_channel_quick()
        case 'other':
            return m.order_channel_other()
        default:
            return channel
    }
}

function OrdersPage() {
    const navigate = useNavigate({ from: '/orders/' })
    const urlSearch = Route.useSearch()
    useRememberListSearch(LIST_SEARCH_KEYS.orders, urlSearch)
    const setSearch = (
        updater: (prev: OrdersSearch) => OrdersSearch,
        opts?: { replace?: boolean },
    ) => void navigate({ search: updater, replace: opts?.replace ?? false })

    // Ô tìm kiếm giữ state cục bộ để gõ mượt, debounce rồi mới đẩy lên URL —
    // tránh spam history mỗi ký tự (trước đây mỗi ký tự là 1 request list order).
    const [searchInput, setSearchInput] = useState(urlSearch.q ?? '')
    const debouncedSearch = useDebounced(searchInput, 300)
    useEffect(() => {
        if (debouncedSearch === (urlSearch.q ?? '')) return
        setSearch(
            (prev) => ({
                ...prev,
                q: debouncedSearch || undefined,
                page: undefined,
            }),
            { replace: true },
        )
    }, [debouncedSearch])
    useEffect(() => {
        setSearchInput(urlSearch.q ?? '')
    }, [urlSearch.q])

    const status = urlSearch.status ?? ''
    const paymentStatus = urlSearch.payment_status ?? ''
    const settlementIntent = urlSearch.settlement_intent ?? ''
    const dateFrom = urlSearch.date_from ?? ''
    const dateTo = urlSearch.date_to ?? ''
    const page = urlSearch.page ?? 1

    const [qrOpen, setQrOpen] = useState(false)
    const [filterDialogOpen, setFilterDialogOpen] = useState(false)
    const [exportDialogOpen, setExportDialogOpen] = useState(false)
    const { hasPermission } = useAuth()

    // Ngày xuất Excel — mặc định hôm nay theo giờ địa phương ('en-CA' → YYYY-MM-DD).
    const [exportDate, setExportDate] = useState(() =>
        new Date().toLocaleDateString('en-CA'),
    )
    const [exporting, setExporting] = useState(false)
    // Mặc định chỉ xuất đơn đã/1 phần thanh toán; bỏ tick → xuất hết trừ đơn huỷ.
    const [exportPaidOnly, setExportPaidOnly] = useState(true)

    const handleExportDaily = async () => {
        if (!exportDate) {
            toast.error(m.order_export_pick_date())
            return
        }
        setExporting(true)
        try {
            const res = await salesOrderApi.exportDailyExcel(
                exportDate,
                exportPaidOnly,
            )
            downloadBlob(
                res.data,
                filenameFromContentDisposition(
                    res.headers['content-disposition'] as string | undefined,
                ) ?? `bao-cao-don-hang-ngay-${exportDate}.xlsx`,
            )
            setExportDialogOpen(false)
        } catch {
            toast.error(m.order_export_failed())
        } finally {
            setExporting(false)
        }
    }

    const { data, isLoading } = useSalesOrders({
        search: urlSearch.q || undefined,
        status: status || undefined,
        payment_status: paymentStatus || undefined,
        settlement_intent: settlementIntent || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page,
        per_page: 20,
    })

    const orders = data?.data ?? []
    const meta = data?.meta
    const renderFilters = () => (
        <>
            <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder={m.order_search_placeholder()}
                    className="pl-9"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
            </div>
            <Select
                value={status || 'all'}
                onValueChange={(v) =>
                    setSearch((prev) => ({
                        ...prev,
                        status:
                            v === 'all' ? undefined : (v as SalesOrderStatus),
                        page: undefined,
                    }))
                }
            >
                <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder={m.order_status_all()} />
                </SelectTrigger>
                <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Select
                value={paymentStatus || 'all'}
                onValueChange={(v) =>
                    setSearch((prev) => ({
                        ...prev,
                        payment_status:
                            v === 'all' ? undefined : (v as PaymentFilter),
                        page: undefined,
                    }))
                }
            >
                <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder={m.order_payment_all()} />
                </SelectTrigger>
                <SelectContent>
                    {PAYMENT_FILTER_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Select
                value={settlementIntent || 'all'}
                onValueChange={(v) =>
                    setSearch((prev) => ({
                        ...prev,
                        settlement_intent:
                            v === 'all' ? undefined : (v as SettlementFilter),
                        page: undefined,
                    }))
                }
            >
                <SelectTrigger className="w-full md:w-48">
                    <SelectValue
                        placeholder={m.order_settlement_filter_all()}
                    />
                </SelectTrigger>
                <SelectContent>
                    {SETTLEMENT_INTENT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3 md:flex">
                <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) =>
                        setSearch((prev) => ({
                            ...prev,
                            date_from: e.target.value || undefined,
                            page: undefined,
                        }))
                    }
                    className="w-full md:w-40"
                    title={m.report_from_date()}
                />
                <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) =>
                        setSearch((prev) => ({
                            ...prev,
                            date_to: e.target.value || undefined,
                            page: undefined,
                        }))
                    }
                    className="w-full md:w-40"
                    title={m.report_to_date()}
                />
            </div>
        </>
    )

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ListPageHeader
                icon={<ShoppingCart className="size-5 shrink-0" />}
                title={m.order_title()}
                utilityActions={
                    <Button
                        variant="outline"
                        size="icon"
                        className="md:hidden"
                        onClick={() => setFilterDialogOpen(true)}
                    >
                        <Filter className="size-4" />
                    </Button>
                }
                primaryActions={
                    <>
                        {hasPermission('invoices.export') && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setExportDialogOpen(true)}
                            >
                                {m.order_export_daily_excel()}
                            </Button>
                        )}
                        {hasPermission('sales_orders.read') && (
                            <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={() => setQrOpen(true)}
                            >
                                <QrCode className="size-4" />
                                {m.nav_scan_qr()}
                            </Button>
                        )}
                        <Button asChild size="sm">
                            <Link to="/orders/new" className="w-full">
                                <Plus className="size-4" />
                                {m.order_create_btn()}
                            </Link>
                        </Button>
                    </>
                }
            />

            {/* Filters */}
            <div className="hidden md:flex md:flex-wrap gap-3">
                {renderFilters()}
            </div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{m.order_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Popup chọn ngày để xuất Excel — thay cho ô input ngày cố định. */}
            <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>
                            {m.order_export_dialog_title()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-2 py-2">
                        <label
                            htmlFor="order-export-date"
                            className="text-sm font-medium"
                        >
                            {m.order_export_date_label()}
                        </label>
                        <Input
                            id="order-export-date"
                            type="date"
                            value={exportDate}
                            onChange={(e) => setExportDate(e.target.value)}
                        />
                    </div>
                    <label className="flex items-start gap-2 py-1 text-sm">
                        <input
                            type="checkbox"
                            className="mt-0.5 size-4 accent-primary"
                            checked={exportPaidOnly}
                            onChange={(e) =>
                                setExportPaidOnly(e.target.checked)
                            }
                        />
                        <span>{m.order_export_paid_only_label()}</span>
                    </label>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="flex-1 sm:flex-none"
                            onClick={() => setExportDialogOpen(false)}
                            disabled={exporting}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            className="flex-1 sm:flex-none"
                            onClick={handleExportDaily}
                            disabled={exporting || !exportDate}
                        >
                            {m.order_export_submit()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Table */}
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.order_code()}</TableHead>
                            <TableHead>{m.order_internal_code()}</TableHead>
                            <TableHead>{m.order_customer()}</TableHead>
                            <TableHead>{m.order_channel()}</TableHead>
                            <TableHead>{m.order_total()}</TableHead>
                            <TableHead>{m.order_payment()}</TableHead>
                            <TableHead>{m.order_status()}</TableHead>
                            <TableHead>{m.order_sla_label()}</TableHead>
                            <TableHead>{m.order_created_date()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : orders.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.order_no_data()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            orders.map((order) => (
                                <TableRow key={order.id}>
                                    <TableCell>
                                        <Link
                                            to="/orders/$orderId"
                                            params={{ orderId: order.id }}
                                            className="font-mono text-xs text-primary hover:underline"
                                        >
                                            {order.order_code}
                                        </Link>
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                        {order.internal_order_code || (
                                            <span className="text-muted-foreground">
                                                —
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {order.customer_id ? (
                                            <div>
                                                <Link
                                                    to="/customers/$customerId"
                                                    params={{
                                                        customerId:
                                                            order.customer_id,
                                                    }}
                                                    className="font-medium text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary"
                                                >
                                                    {order.customer_name}
                                                </Link>
                                                <div className="text-xs text-muted-foreground">
                                                    {order.customer_phone}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="font-medium">
                                                    {order.customer_name}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {order.customer_phone}
                                                </div>
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {getChannelLabel(order.source_channel)}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {formatVnd(
                                            Math.round(order.total_amount),
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={
                                                order.payment_status === 'paid'
                                                    ? 'success'
                                                    : order.payment_status ===
                                                        'partial'
                                                      ? 'warning'
                                                      : 'outline'
                                            }
                                        >
                                            {PAYMENT_LABELS[
                                                order.payment_status
                                            ] ?? order.payment_status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <OrderStatusBadge
                                            status={order.status}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <SLABadge
                                            deadlineAt={order.sla_deadline_at}
                                            breached={order.sla_breached}
                                            warningAt={order.sla_warning_at}
                                        />
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {new Date(
                                            order.created_at,
                                        ).toLocaleDateString('vi-VN')}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.order_total_count({ count: String(meta.total) })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() =>
                                setSearch((prev) => ({
                                    ...prev,
                                    page: page - 1 > 1 ? page - 1 : undefined,
                                }))
                            }
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() =>
                                setSearch((prev) => ({
                                    ...prev,
                                    page: page + 1,
                                }))
                            }
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}
            <QRScannerDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                mode="navigate"
            />
        </div>
    )
}
